// SPDX-FileCopyrightText: AIGENERATED/GitHub Copilot
// SPDX-License-Identifier: LicenseRef-OpAiGenerated
import {
  Alert,
  Form,
  FormRow,
  Loading,
  Option,
  SegmentedButton,
  Tile,
} from '@op/opds.opux';
import { Stepper } from '@op/opds.opux-stepper';
import { useEffect, useState } from 'react';
import {
  calculateTemporaryDisabilityEstimate,
  type EstimatorPremium,
} from 'api/calculator';
import styles from 'App.module.scss';
import { formLocalDateUTC, getInitialState } from 'utils';

const FALLBACK_DOB = '1985-03-15';
const MONTHLY_INCOME = 3000;
const WAITING_DAYS = [21, 30, 60, 90];
const DEFAULT_WAITING_DAYS = 30;
const DEFAULT_BENEFIT = 75;
const MIN_BENEFIT = 30;
const MAX_BENEFIT = 120;

function EstimatorView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [premiums, setPremiums] = useState<EstimatorPremium[]>([]);
  const [selectedBenefit, setSelectedBenefit] = useState(DEFAULT_BENEFIT);
  const [selectedWaitingDays, setSelectedWaitingDays] =
    useState(DEFAULT_WAITING_DAYS);

  useEffect(() => {
    const dobString =
      getInitialState().partyInformation?.dateOfBirth ?? FALLBACK_DOB;
    const dateOfBirth = formLocalDateUTC(new Date(dobString));

    calculateTemporaryDisabilityEstimate({
      dateOfBirth,
      monthlyIncome: MONTHLY_INCOME,
    })
      .then(({ premiums: result, kelaDailyAllowance }) => {
        setPremiums(result);
        if (kelaDailyAllowance) {
          setSelectedBenefit(
            Math.min(MAX_BENEFIT, Math.max(MIN_BENEFIT, kelaDailyAllowance))
          );
        }
        setLoading(false);
        return result;
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const selectedPremium = premiums.find(
    (p) =>
      p.insuranceDailyAllowance === selectedBenefit &&
      p.waitingDays === selectedWaitingDays
  );

  return (
    <div
      data-testid='estimator-view'
      className={`${styles.root as string} opds opux-v6__opds opds-styles-v1-elements-headings-small opds-styles-v1-text-body-large`}>
      <h1>Työkyvyttömyyslaskuri</h1>
      {loading && <Loading />}
      {error && <Alert type='critical'>Hinnoittelu epäonnistui</Alert>}
      {!loading && !error && (
        <Form stacked>
          <FormRow label='Karenssi (päivät)'>
            <SegmentedButton
              data-testid='waiting-days-selector'
              value={String(selectedWaitingDays)}
              onChange={(_ev, val) => {
                setSelectedWaitingDays(Number(val));
              }}>
              {WAITING_DAYS.map((days) => (
                <Option key={days} value={String(days)}>
                  {days}
                </Option>
              ))}
            </SegmentedButton>
          </FormRow>
          <FormRow label='Päiväkorvaus'>
            <Stepper
              data-testid='benefit-stepper'
              value={selectedBenefit}
              min={MIN_BENEFIT}
              max={MAX_BENEFIT}
              step={10}
              unit='€'
              onChange={(newValue) => {
                setSelectedBenefit(newValue);
              }}
            />
          </FormRow>
          {selectedPremium !== undefined ? (
            <Tile title='Kuukausimaksu'>
              <p data-testid='premium-amount'>
                {selectedPremium.monthlyPremium.amount} €/kk
              </p>
            </Tile>
          ) : (
            <Alert type='information' embedded>
              Valitulle yhdistelmälle ei löydy hinnoittelua.
            </Alert>
          )}
        </Form>
      )}
    </div>
  );
}

export default EstimatorView;
